import { RefObject, useCallback, useEffect, useState } from "react"
import { DashBoardCondition } from "features/dashBoard/types"
import { PlotData, parsePlotData, useScatterPlotGraphModel } from "./model"

import EChartsReactCore from "echarts-for-react/lib/core"

type useScatterPlotGraphEventsProps<T> = {
	echartRef: RefObject<EChartsReactCore | null>
	graphData: ReturnType<typeof useScatterPlotGraphModel<T>>
	onSelected: (id: string) => void
	onChangeCondition: (
    axisKey?: string,
    changed?: DashBoardCondition,
    toggle?: boolean
  ) => void
}
export const useScatterPlotGraphEvents = <T,>({
	echartRef,
	graphData,
	onSelected,
	onChangeCondition
}: useScatterPlotGraphEventsProps<T>) => {
  const [selectedIdentifier, setSelectedIdentifier] = useState<{
    seriesIndex: number
    dataIndex: number
  }[] | undefined>(undefined)

	const [brushArea, setBrushArea] = useState<{
    brushType: string
    range: number[][]
  } | null>(null)

	const onClickGraph = useCallback(
    (obj: { value?: PlotData }) => {
      const value = obj.value
      if (value && value[3]) onSelected(value[3])
      return false
    },
    [onSelected]
  )

  const onBrushSelected = useCallback(
    (state: {
      batch: {
        selected: {
          dataIndex: number[],
          seriesIndex: number
        }[]
      }[]
    }) => {
      setSelectedIdentifier(state.batch.flatMap(
        (batch) => batch.selected.flatMap(
          (selected) => selected.dataIndex.map(
            (dataIndex) => ({
              seriesIndex: selected.seriesIndex,
              dataIndex
            })
          )
        )
      ));
    },
    [setSelectedIdentifier]
  )

	const onBrushEnd = useCallback(
    (state: {
      areas: {
        brushType: string,
        range: number[][]
      }[]
    }) => {
      if (!state?.areas || state.areas.length === 0) return
      setBrushArea(state.areas[0])
      //console.log('onBrushEnd', state)
    },
    [setBrushArea]
  )
  
	useEffect(
    () => {
      if (brushArea === null) return

      const identifiers = selectedIdentifier?.flatMap(
        ({ seriesIndex, dataIndex }) => {
          const series = graphData?.series[seriesIndex]
          const data = series?.data[dataIndex]
          if (!data) return []
          const identifier = parsePlotData(data).id
          return identifier ?? []
        }
      ) || []
      onChangeCondition(
        "identifier",
        identifiers?.length ? {
          code: identifiers,
          label: `${identifiers.length}件`
        } : undefined
      )
      const setArea = {...brushArea}
      setBrushArea(null)
      setTimeout(() => {
        const echart = echartRef.current?.getEchartsInstance()
				echart?.dispatchAction({
          type: 'brush',
          command: setArea.brushType,
          areas: [setArea]
        }, {
          silent: true
        })
        //console.log('re-set brush area')
      }, 100)
    },
    [brushArea, onChangeCondition, graphData, selectedIdentifier, echartRef]
  )
	return {
		brushselected: onBrushSelected,
		brushend: onBrushEnd,
		click: onClickGraph
	}
}